import { useRef, type CSSProperties, type Dispatch, type KeyboardEvent, type PointerEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { getFontFile, getTemplateAssetFile, type Font, type TemplateAsset, type TemplateDefinition } from "@/api/client";
import { EmptyState } from "@/components/ui";
import { useObjectURL } from "@/components/useObjectURL";
import { fontFaceSupported, useFontFace } from "@/features/fonts/fontFace";
import type { DesignerAction } from "@/features/templates/designer/definition";
import { alignOf, fieldLabel, fontFamilyName, fontFor } from "@/features/templates/designer/fields";
import {
  DEFAULT_SIZE_PT,
  PT_TO_MM,
  fieldBox,
  fitCentred,
  innerBox,
  mmToPx,
  pageSizeMM,
  pxToMM,
  type Box,
  type Field,
} from "@/features/templates/designer/geometry";
import { sampleText } from "@/features/templates/designer/samples";

export type CanvasProps = {
  definition: TemplateDefinition;
  zoom: number;
  fonts: Font[];
  assets: TemplateAsset[];
  certType: string;
  now: Date;
  selected: number | undefined;
  /** Fields with a problem, outlined in red and marked aria-invalid. */
  invalid: ReadonlySet<number>;
  readOnly: boolean;
  onSelect: (index: number | undefined) => void;
  dispatch: Dispatch<DesignerAction>;
};

const HELVETICA = "Helvetica, Arial, sans-serif";
// gofpdf pads a cell's text 1 mm from its left or right edge (its default cell margin).
const CELL_MARGIN_MM = 1;
const JUSTIFY = { left: "flex-start", center: "center", right: "flex-end" } as const;

export function Canvas({ definition, zoom, fonts, assets, certType, now, selected, invalid, readOnly, onSelect, dispatch }: CanvasProps) {
  const pageRef = useRef<HTMLDivElement>(null);
  const size = pageSizeMM(definition.page);
  if (!size) {
    return <EmptyState title="This page can't be drawn" detail="Choose a page size and orientation under Page." compact />;
  }
  const px = (mm: number) => mmToPx(mm, zoom);
  const place = (b: Box): CSSProperties => ({ left: px(b.x), top: px(b.y), width: px(b.w), height: px(b.h) });
  const asset = (id: string | undefined) => assets.find((a) => a.id === id);
  const background = asset(definition.background?.asset_key);
  const watermark = asset(definition.watermark?.asset_key);
  const usedFonts = new Map<string, Font>();
  for (const field of definition.fields) {
    const font = field.type === "text" ? fontFor(field, fonts) : undefined;
    if (font) usedFonts.set(font.id, font);
  }

  return (
    <div className="overflow-auto rounded-lg border border-zinc-200 bg-zinc-100 p-6">
      {[...usedFonts.values()].map((font) => (
        <FontLoader key={font.id} font={font} />
      ))}
      <div
        ref={pageRef}
        role="region"
        aria-label={`Page, ${definition.page.size} ${definition.page.orientation}`}
        // Takes focus after a delete, so keys don't reach the next field reusing the node.
        tabIndex={-1}
        className="relative mx-auto bg-white shadow-sm outline-none ring-1 ring-zinc-300 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-700"
        style={{ width: px(size.w), height: px(size.h) }}
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) onSelect(undefined);
        }}
      >
        {background ? (
          <AssetImage
            asset={background}
            alt={`Background: ${background.name}`}
            className="pointer-events-none absolute left-0 top-0"
            style={{ width: "100%", height: "100%" }}
          />
        ) : null}
        {watermark && definition.watermark ? (
          <AssetImage
            asset={watermark}
            alt={`Watermark: ${watermark.name}`}
            className="pointer-events-none absolute"
            style={{
              ...place(fitCentred(innerBox(definition.page), watermark.width_px, watermark.height_px)),
              opacity: definition.watermark.opacity,
            }}
          />
        ) : null}
        <div
          data-testid="margin-guides"
          aria-hidden="true"
          className="pointer-events-none absolute border border-dashed border-sky-400/70"
          style={place(innerBox(definition.page))}
        />
        {definition.fields.map((field, index) => {
          const b = fieldBox(field, definition.page);
          return b ? (
            <CanvasField
              key={index}
              index={index}
              field={field}
              selected={index === selected}
              invalid={invalid.has(index)}
              readOnly={readOnly}
              onSelect={onSelect}
              onRemoved={() => pageRef.current?.focus()}
              dispatch={dispatch}
              style={place(b)}
              zoom={zoom}
              label={fieldLabel(field, assets)}
              asset={asset(field.asset_key)}
              font={fontFor(field, fonts)}
              text={sampleText(field.bind, { certType, now, format: field.format })}
            />
          ) : null;
        })}
      </div>
    </div>
  );
}

type Gesture = {
  pointerId: number;
  x: number;
  y: number;
  mode: "move" | Resize;
  origin: Field;
  key: string;
};
type Resize = "wh" | "w" | "size";

// A fresh coalesce key per gesture makes each drag one undo step.
let gestures = 0;

/** Which handle a selected field gets: both sides, width only (text with w_mm), or a square (qr). */
function resizeMode(field: Field): Resize | null {
  if (field.type === "qr") return "size";
  if (field.type === "text") return field.w_mm !== undefined ? "w" : null;
  return "wh";
}

function CanvasField({
  index,
  field,
  selected,
  invalid,
  readOnly,
  onSelect,
  onRemoved,
  dispatch,
  style,
  zoom,
  label,
  asset,
  font,
  text,
}: {
  index: number;
  field: Field;
  selected: boolean;
  invalid: boolean;
  readOnly: boolean;
  onSelect: (index: number | undefined) => void;
  onRemoved: () => void;
  dispatch: Dispatch<DesignerAction>;
  style: CSSProperties;
  zoom: number;
  label: string;
  asset: TemplateAsset | undefined;
  font: Font | undefined;
  text: string;
}) {
  const gesture = useRef<Gesture | null>(null);
  const handle = selected && !readOnly ? resizeMode(field) : null;

  const begin = (e: PointerEvent<HTMLElement>, mode: Gesture["mode"]) => {
    if (e.button !== 0) return;
    onSelect(index);
    if (readOnly) return;
    // Capture keeps the gesture going when the pointer outruns the field.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesture.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, mode, origin: field, key: `gesture-${++gestures}` };
  };
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dx = pxToMM(e.clientX - g.x, zoom);
    const dy = pxToMM(e.clientY - g.y, zoom);
    const o = g.origin;
    if (g.mode === "move") {
      dispatch({ type: "move", index, x_mm: o.x_mm + dx, y_mm: o.y_mm + dy, coalesce: g.key });
    } else if (g.mode === "size") {
      dispatch({ type: "resize", index, size_mm: (o.size_mm ?? 0) + (Math.abs(dx) > Math.abs(dy) ? dx : dy), coalesce: g.key });
    } else if (g.mode === "w") {
      dispatch({ type: "resize", index, w_mm: (o.w_mm ?? 0) + dx, coalesce: g.key });
    } else {
      dispatch({ type: "resize", index, w_mm: (o.w_mm ?? 0) + dx, h_mm: (o.h_mm ?? 0) + dy, coalesce: g.key });
    }
  };
  const end = (e: PointerEvent<HTMLElement>) => {
    if (gesture.current?.pointerId === e.pointerId) gesture.current = null;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (readOnly) return;
    const step = e.shiftKey ? 10 : 1;
    const nudge = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (nudge) {
      e.preventDefault();
      dispatch({ type: "move", index, x_mm: field.x_mm + (nudge[0] ?? 0), y_mm: field.y_mm + (nudge[1] ?? 0) });
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      dispatch({ type: "remove", index });
      onSelect(undefined);
      onRemoved();
    }
  };

  const ring = selected
    ? `z-10 outline-2 outline-offset-1 ${invalid ? "outline-red-600" : "outline-sky-500"}`
    : invalid
      ? "outline-2 outline-red-500"
      : "hover:outline-1 hover:outline-sky-300";
  const common = {
    role: "button",
    tabIndex: 0,
    "aria-label": label,
    "aria-pressed": selected,
    "aria-invalid": invalid || undefined,
    onPointerDown: (e: PointerEvent<HTMLElement>) => begin(e, "move"),
    onPointerMove,
    onPointerUp: end,
    onPointerCancel: end,
    onFocus: () => onSelect(index),
    onKeyDown,
    className: `absolute select-none touch-none outline-none focus-visible:outline-2 focus-visible:outline-sky-700 ${ring} ${
      readOnly ? "cursor-default" : "cursor-move"
    }`,
  };
  const handleEl = handle ? (
    <span
      data-testid="resize-handle"
      aria-hidden="true"
      onPointerDown={(e) => {
        e.stopPropagation();
        begin(e, handle);
      }}
      className={
        handle === "w"
          ? "absolute -right-1.5 top-1/2 h-4 w-2 -translate-y-1/2 cursor-ew-resize rounded-sm border border-white bg-sky-500"
          : "absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-nwse-resize rounded-sm border border-white bg-sky-500"
      }
    />
  ) : null;

  if (field.type === "text") {
    const fontStyle = field.font_style ?? "regular";
    const textStyle: CSSProperties = {
      fontFamily: font ? `"${fontFamilyName(font)}", ${HELVETICA}` : HELVETICA,
      fontSize: mmToPx((field.size_pt ?? DEFAULT_SIZE_PT) * PT_TO_MM, zoom),
      // An uploaded file is already the bold or italic face; only Helvetica needs the style applied.
      fontWeight: !font && fontStyle.startsWith("bold") ? "bold" : "normal",
      fontStyle: !font && fontStyle.endsWith("italic") ? "italic" : "normal",
      lineHeight: 1,
      whiteSpace: "pre",
      color: "rgb(20, 24, 31)",
    };
    return (
      <div
        {...common}
        className={`${common.className} flex items-center`}
        style={{ ...style, justifyContent: JUSTIFY[alignOf(field)], padding: `0 ${mmToPx(CELL_MARGIN_MM, zoom)}px` }}
      >
        <span style={textStyle}>{text}</span>
        {handleEl}
      </div>
    );
  }
  if (field.type === "image" && field.asset_key) {
    return (
      <div {...common} style={style}>
        {asset ? (
          <AssetImage asset={asset} alt={asset.name} className="block" style={{ width: "100%", height: "100%" }} />
        ) : (
          <Placeholder label="Image not found" tone="missing" />
        )}
        {handleEl}
      </div>
    );
  }
  return (
    <div {...common} style={style}>
      <Placeholder label={field.type === "qr" ? "QR code" : field.type === "signature" ? "Signature" : "Photo"} />
      {handleEl}
    </div>
  );
}

function Placeholder({ label, tone = "default" }: { label: string; tone?: "default" | "missing" }) {
  const cls = tone === "missing" ? "border-red-400 bg-red-50/70 text-red-700" : "border-zinc-400 bg-zinc-50/70 text-zinc-600";
  return (
    <span className={`grid h-full w-full place-items-center overflow-hidden border border-dashed text-[10px] leading-none ${cls}`}>
      {label}
    </span>
  );
}

function AssetImage({ asset, alt, className, style }: { asset: TemplateAsset; alt: string; className: string; style: CSSProperties }) {
  // Same key as the image library, so each file is fetched once per session.
  const file = useQuery({
    queryKey: ["template-asset-file", asset.id],
    queryFn: () => getTemplateAssetFile(asset.id),
    staleTime: Infinity,
  });
  const url = useObjectURL(file.data);
  if (!url) return null;
  // The renderer stretches an image to its box, so the canvas does not keep the aspect ratio either.
  return <img src={url} alt={alt} draggable={false} className={className} style={{ ...style, objectFit: "fill" }} />;
}

function FontLoader({ font }: { font: Font }) {
  const file = useQuery({
    queryKey: ["font-file", font.id],
    queryFn: () => getFontFile(font.id),
    enabled: fontFaceSupported(),
    staleTime: Infinity,
  });
  useFontFace(fontFamilyName(font), file.data);
  return null;
}
